import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Modal from '../components/Modal';
import axios from '../services/axios';
import { formatApiDate } from '../utils/date';

const meses = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

const formatearMonto = (valor) => {
  const numero = Number(valor) || 0;
  return numero.toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatearPeriodo = (mes, anio) => {
  const etiqueta = meses.find((item) => item.value === Number(mes))?.label || 'Mes';
  return `${etiqueta} ${anio || ''}`.trim();
};

const formatearFecha = (fecha) => {
  if (!fecha) return '-';
  return formatApiDate(fecha);
};

const formatearNumeroCuota = (valor) => String(valor).padStart(2, '0');

const CargosManualesClienteAdmin = () => {
  const { clienteId } = useParams();
  const navigate = useNavigate();
  const hoy = new Date();
  const anios = Array.from({ length: 7 }, (_, i) => hoy.getFullYear() - 2 + i);

  const [loading, setLoading] = useState(true);
  const [cliente, setCliente] = useState(null);
  const [cargos, setCargos] = useState([]);
  const [loadingCargos, setLoadingCargos] = useState(false);
  const [loadingCrear, setLoadingCrear] = useState(false);
  const [loadingAccionId, setLoadingAccionId] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [cargoEditando, setCargoEditando] = useState(null);

  const [mesFiltro, setMesFiltro] = useState('todos');
  const [anioFiltro, setAnioFiltro] = useState('todos');

  const [mesCargo, setMesCargo] = useState(hoy.getMonth() + 1);
  const [anioCargo, setAnioCargo] = useState(hoy.getFullYear());
  const [montoCargo, setMontoCargo] = useState('');
  const [descripcionCargo, setDescripcionCargo] = useState('');

  const [planDescripcionBase, setPlanDescripcionBase] = useState('');
  const [planMontoTotal, setPlanMontoTotal] = useState('');
  const [planCuotas, setPlanCuotas] = useState(2);
  const [planInicioMes, setPlanInicioMes] = useState(hoy.getMonth() + 1);
  const [planInicioAnio, setPlanInicioAnio] = useState(hoy.getFullYear());

  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
    confirmText: 'Aceptar',
    onConfirm: null,
    onCancel: null,
  });

  const cerrarModal = () => setModal((prev) => ({ ...prev, isOpen: false }));

  const limpiarFormularioCargo = () => {
    setCargoEditando(null);
    setMesCargo(hoy.getMonth() + 1);
    setAnioCargo(hoy.getFullYear());
    setMontoCargo('');
    setDescripcionCargo('');
  };

  useEffect(() => {
    cargarCliente();
    cargarCargos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const cargarCliente = async () => {
    setLoading(true);
    try {
      const clienteRes = await axios.get(`/usuarios/${clienteId}`);
      setCliente(clienteRes.data);
    } catch (error) {
      console.error('Error al cargar cliente:', error);
      setCliente(null);
      setModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'No se pudieron cargar los datos del cliente.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
    } finally {
      setLoading(false);
    }
  };

  const cargarCargos = async () => {
    setLoadingCargos(true);
    try {
      const response = await axios.get(`/tarifario/cargos-manuales/cliente/${clienteId}`);
      setCargos(response.data || []);
    } catch (error) {
      console.error('Error al cargar cargos manuales:', error);
      setCargos([]);
    } finally {
      setLoadingCargos(false);
    }
  };

  const cargosFiltrados = useMemo(() => {
    return [...cargos]
      .filter((cargo) => (
        (mesFiltro === 'todos' || cargo.mes === Number(mesFiltro)) &&
        (anioFiltro === 'todos' || cargo.anio === Number(anioFiltro))
      ))
      .sort((a, b) => {
        if (a.anio !== b.anio) return b.anio - a.anio;
        if (a.mes !== b.mes) return b.mes - a.mes;
        const fechaA = new Date(a.created_at || 0).getTime();
        const fechaB = new Date(b.created_at || 0).getTime();
        return fechaA - fechaB;
      });
  }, [cargos, mesFiltro, anioFiltro]);

  const handleGuardarCargoManual = () => {
    if (!cliente) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Cliente requerido',
        message: 'No se encontro el cliente seleccionado.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
      return;
    }

    const montoNumero = Number(montoCargo);
    const estaEditando = Boolean(cargoEditando);
    if (!montoNumero) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Monto invalido',
        message: 'Ingrese un monto valido distinto de cero. Use valores negativos para aplicar descuentos.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
      return;
    }

    setModal({
      isOpen: true,
      type: 'warning',
      title: estaEditando ? 'Confirmar cambios' : 'Confirmar cargo manual',
      message: estaEditando
        ? `Se modificara el cargo manual del cliente ${cliente.padron} - ${cliente.nombre}.

Nuevo periodo: ${mesCargo}/${anioCargo}
Nuevo monto: $${formatearMonto(montoNumero)}

- No se modifican boletas pagadas, anuladas o en plan de pago

Desea continuar?`
        : `Se agregara ${montoNumero < 0 ? `un descuento de $${formatearMonto(Math.abs(montoNumero))}` : `un cargo manual de $${formatearMonto(montoNumero)}`} al cliente ${cliente.padron} - ${cliente.nombre} para el periodo ${mesCargo}/${anioCargo}.

- No se modifican boletas pagadas
- Si no hay boleta generada, se aplicara cuando se genere

Desea continuar?`,
      confirmText: estaEditando ? 'Guardar cambios' : (montoNumero < 0 ? 'Aplicar descuento' : 'Aplicar cargo'),
      onConfirm: async () => {
        cerrarModal();
        setLoadingCrear(true);

        try {
          const payload = {
            mes: Number(mesCargo),
            anio: Number(anioCargo),
            monto: montoNumero,
            descripcion: descripcionCargo?.trim() || undefined
          };

          const response = estaEditando
            ? await axios.put(`/tarifario/cargos-manuales/${cargoEditando.id}`, payload)
            : await axios.post('/tarifario/cargos-manuales', {
              usuarioId: cliente.id,
              ...payload,
            });

          const resultado = response.data;

          setModal({
            isOpen: true,
            type: 'success',
            title: estaEditando ? 'Cargo actualizado' : 'Cargo aplicado',
            message: estaEditando
              ? `El cargo manual se actualizo correctamente.

Boletas actualizadas: ${resultado.boletasActualizadas}`
              : `El cargo manual se registro correctamente.

Boletas actualizadas: ${resultado.boletasActualizadas}`,
            onConfirm: cerrarModal
          });

          limpiarFormularioCargo();
          cargarCargos();
        } catch (error) {
          console.error('Error al guardar cargo manual:', error);
          setModal({
            isOpen: true,
            type: 'error',
            title: 'Error',
            message: error.response?.data?.message || 'No se pudo guardar el cargo manual.',
            onConfirm: cerrarModal
          });
        } finally {
          setLoadingCrear(false);
        }
      },
      onCancel: cerrarModal
    });
  };

  const handleEditarCargo = (cargo) => {
    setCargoEditando(cargo);
    setMesCargo(Number(cargo.mes));
    setAnioCargo(Number(cargo.anio));
    setMontoCargo(String(cargo.monto));
    setDescripcionCargo(cargo.descripcion || '');
  };

  const handleEliminarCargo = (cargo) => {
    const detallePlan = cargo.plan_pago_grupo_id
      ? '\n\nEste cargo pertenece a un plan de cargos en cuotas.'
      : '';

    setModal({
      isOpen: true,
      type: 'warning',
      title: 'Eliminar cargo manual',
      message: `Se eliminara el cargo de ${formatearPeriodo(cargo.mes, cargo.anio)} por $${formatearMonto(cargo.monto)}.${detallePlan}

- No se modifican boletas pagadas, anuladas o en plan de pago
- Si hay una boleta editable del periodo, se recalculara el total

Desea continuar?`,
      confirmText: 'Eliminar cargo',
      onConfirm: async () => {
        cerrarModal();
        setLoadingAccionId(cargo.id);

        try {
          const response = await axios.delete(`/tarifario/cargos-manuales/${cargo.id}`);
          const resultado = response.data || {};

          if (cargoEditando?.id === cargo.id) {
            limpiarFormularioCargo();
          }

          setModal({
            isOpen: true,
            type: 'success',
            title: 'Cargo eliminado',
            message: `El cargo manual se elimino correctamente.

Boletas actualizadas: ${resultado.boletasActualizadas || 0}`,
            onConfirm: cerrarModal
          });
          cargarCargos();
        } catch (error) {
          console.error('Error al eliminar cargo manual:', error);
          setModal({
            isOpen: true,
            type: 'error',
            title: 'Error',
            message: error.response?.data?.message || 'No se pudo eliminar el cargo manual.',
            onConfirm: cerrarModal
          });
        } finally {
          setLoadingAccionId(null);
        }
      },
      onCancel: cerrarModal
    });
  };

  const handleCrearPlan = () => {
    if (!cliente) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Cliente requerido',
        message: 'No se encontro el cliente seleccionado.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
      return;
    }

    const montoTotalNumero = Number(planMontoTotal);
    const cuotasNumero = Number(planCuotas);
    const descripcionBase = planDescripcionBase?.trim();

    if (!descripcionBase) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Descripcion requerida',
        message: 'Ingrese una descripcion base para el plan.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
      return;
    }

    if (!montoTotalNumero) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Monto invalido',
        message: 'Ingrese un monto total valido distinto de cero. Use valores negativos para aplicar descuentos.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
      return;
    }

    if (!Number.isFinite(cuotasNumero) || cuotasNumero < 1 || !Number.isInteger(cuotasNumero)) {
      setModal({
        isOpen: true,
        type: 'warning',
        title: 'Cuotas invalidas',
        message: 'Ingrese una cantidad de cuotas valida.',
        onConfirm: () => setModal({ ...modal, isOpen: false })
      });
      return;
    }

    const ejemploDescripcion = cuotasNumero > 1
      ? `${descripcionBase} ${formatearNumeroCuota(1)}/${formatearNumeroCuota(cuotasNumero)}`
      : descripcionBase;

    const montoCuotaEstimado = cuotasNumero > 0
      ? formatearMonto(montoTotalNumero / cuotasNumero)
      : '0,00';

    setModal({
      isOpen: true,
      type: 'warning',
      title: 'Confirmar plan de cargos',
      message: `Se crearan ${cuotasNumero} cargos desde ${planInicioMes}/${planInicioAnio} para el cliente ${cliente.padron} - ${cliente.nombre}.

Descripcion base: ${descripcionBase}
Ejemplo: ${ejemploDescripcion}
Monto total: $${formatearMonto(montoTotalNumero)}
Monto estimado por cuota: $${montoCuotaEstimado}

Desea continuar?`,
      confirmText: 'Crear plan',
      onConfirm: async () => {
        setModal({ ...modal, isOpen: false });
        setLoadingPlan(true);

        try {
          const response = await axios.post('/tarifario/cargos-manuales/plan', {
            usuarioId: cliente.id,
            montoTotal: montoTotalNumero,
            cantidadCuotas: cuotasNumero,
            inicioMes: Number(planInicioMes),
            inicioAnio: Number(planInicioAnio),
            descripcionBase: descripcionBase
          });

          const resultado = response.data || {};
          const montoCuota = formatearMonto(resultado.montoCuota || 0);
          const montoCuotaUltima = formatearMonto(resultado.montoCuotaUltima || resultado.montoCuota || 0);
          const detalleUltima = montoCuotaUltima !== montoCuota ? ` (ultima $${montoCuotaUltima})` : '';

          setModal({
            isOpen: true,
            type: 'success',
            title: 'Plan creado',
            message: `Se crearon ${resultado.cantidadCuotas || cuotasNumero} cargos correctamente.

Boletas actualizadas: ${resultado.boletasActualizadas || 0}
Monto por cuota: $${montoCuota}${detalleUltima}`,
            onConfirm: () => setModal({ ...modal, isOpen: false })
          });

          setPlanMontoTotal('');
          setPlanCuotas(2);
          cargarCargos();
        } catch (error) {
          console.error('Error al crear plan de cargos:', error);
          setModal({
            isOpen: true,
            type: 'error',
            title: 'Error',
            message: error.response?.data?.message || 'No se pudo crear el plan de cargos.',
            onConfirm: () => setModal({ ...modal, isOpen: false })
          });
        } finally {
          setLoadingPlan(false);
        }
      },
      onCancel: () => setModal({ ...modal, isOpen: false })
    });
  };

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-symbols-outlined text-primary text-6xl animate-spin">refresh</span>
            <p className="text-[#617c89] mt-4">Cargando cliente...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-symbols-outlined text-red-500 text-6xl">error</span>
            <p className="text-[#617c89] mt-4">Cliente no encontrado</p>
            <button
              onClick={() => navigate('/administrativo/cargos-manuales')}
              className="mt-4 px-4 py-2 bg-primary text-white rounded-lg"
            >
              Volver
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 overflow-auto">
        <div className="bg-white border-b">
          <div className="px-8 py-6 flex items-center gap-3">
            <button
              onClick={() => navigate('/administrativo/cargos-manuales')}
              className="flex items-center justify-center h-10 w-10 rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Detalle de cargos manuales</h1>
              <p className="text-gray-600 mt-1">Cliente {cliente.padron} - {cliente.nombre}</p>
            </div>
          </div>
        </div>

        <div className="p-8 space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Detalle del cliente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700">
              <p><span className="font-semibold">Cliente:</span> {cliente.nombre}</p>
              <p><span className="font-semibold">Padron:</span> {cliente.padron || '-'}</p>
              <p><span className="font-semibold">CUIT:</span> {cliente.cuit || '-'}</p>
              <p><span className="font-semibold">Direccion:</span> {cliente.direccion || '-'}</p>
              <p><span className="font-semibold">Telefono:</span> {cliente.telefono || cliente.whatsapp || '-'}</p>
              <p><span className="font-semibold">Zona:</span> {cliente.zona?.nombre || '-'}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <div className="bg-white rounded-lg border overflow-hidden">
                <div className="px-6 py-4 border-b bg-gray-50 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">Historial de cargos</h3>
                    <p className="text-xs text-gray-500">Total: {cargosFiltrados.length}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <select
                      value={mesFiltro}
                      onChange={(e) => setMesFiltro(e.target.value)}
                      className="h-10 rounded-lg bg-[#f0f3f4] px-3 text-sm text-[#111618] border-none focus:outline-none focus:ring-0"
                    >
                      <option value="todos">Mes: Todos</option>
                      {meses.map((mes) => (
                        <option key={mes.value} value={mes.value}>{mes.label}</option>
                      ))}
                    </select>
                    <select
                      value={anioFiltro}
                      onChange={(e) => setAnioFiltro(e.target.value)}
                      className="h-10 rounded-lg bg-[#f0f3f4] px-3 text-sm text-[#111618] border-none focus:outline-none focus:ring-0"
                    >
                      <option value="todos">Ano: Todos</option>
                      {anios.map((anio) => (
                        <option key={anio} value={anio}>{anio}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {loadingCargos ? (
                  <div className="p-6 text-sm text-gray-500">Cargando cargos...</div>
                ) : cargosFiltrados.length === 0 ? (
                  <div className="p-6 text-sm text-gray-500">No hay cargos manuales registrados.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Periodo</th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Descripcion</th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Monto</th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase">Fecha</th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {cargosFiltrados.map((cargo) => (
                          <tr key={cargo.id} className={cargoEditando?.id === cargo.id ? 'bg-purple-50' : 'hover:bg-gray-50'}>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {formatearPeriodo(cargo.mes, cargo.anio)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              <div className="space-y-1">
                                <p>{cargo.descripcion || 'Cargo manual del cliente'}</p>
                                {cargo.plan_pago_grupo_id && (
                                  <span className="inline-flex rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                                    Plan {cargo.plan_pago_cuota_numero || '-'} / {cargo.plan_pago_cantidad_cuotas || '-'}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                              ${formatearMonto(cargo.monto)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {formatearFecha(cargo.created_at)}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEditarCargo(cargo)}
                                  disabled={loadingCrear || loadingAccionId === cargo.id}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                  title="Editar cargo"
                                >
                                  <span className="material-symbols-outlined text-base">edit</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleEliminarCargo(cargo)}
                                  disabled={loadingCrear || loadingAccionId === cargo.id}
                                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                                  title="Eliminar cargo"
                                >
                                  <span className="material-symbols-outlined text-base">delete</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-lg border p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="material-symbols-outlined text-purple-600">
                    {cargoEditando ? 'edit' : 'add_circle'}
                  </span>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {cargoEditando ? 'Editar cargo manual' : 'Aplicar cargo manual'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {cargoEditando ? `Editando cargo #${cargoEditando.id}` : 'Se aplica al cliente seleccionado'}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Mes</label>
                      <select
                        value={mesCargo}
                        onChange={(e) => setMesCargo(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        {meses.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ano</label>
                      <select
                        value={anioCargo}
                        onChange={(e) => setAnioCargo(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        {anios.map((a) => (
                          <option key={a} value={a}>
                            {a}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Monto * (negativo para descuento)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={montoCargo}
                      onChange={(e) => setMontoCargo(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Ej: 500 o -500 para descuento"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Descripcion (opcional)</label>
                    <input
                      type="text"
                      value={descripcionCargo}
                      onChange={(e) => setDescripcionCargo(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Ej: Aviso por mora"
                    />
                  </div>

                  <p className="text-xs text-gray-500">
                    {cargoEditando
                      ? 'Los cambios recalculan la boleta del periodo si existe y esta editable.'
                      : 'Si no hay boleta generada para el periodo, el cargo quedara pendiente y se aplicara al generar.'}
                  </p>

                  <div className="space-y-2">
                    <button
                      onClick={handleGuardarCargoManual}
                      disabled={loadingCrear}
                      className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {loadingCrear
                        ? (cargoEditando ? 'Guardando...' : 'Aplicando...')
                        : (cargoEditando ? 'Guardar cambios' : 'Aplicar cargo manual')}
                    </button>

                    {cargoEditando && (
                      <button
                        type="button"
                        onClick={limpiarFormularioCargo}
                        disabled={loadingCrear}
                        className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium disabled:opacity-50"
                      >
                        Cancelar edicion
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="material-symbols-outlined text-blue-600">event_repeat</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">Plan de cargos en cuotas</h3>
                    <p className="text-sm text-gray-600">Crea cargos mensuales consecutivos</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Descripcion base</label>
                    <input
                      type="text"
                      value={planDescripcionBase}
                      onChange={(e) => setPlanDescripcionBase(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ej: Reconexion"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Monto total * (negativo para descuentos)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={planMontoTotal}
                      onChange={(e) => setPlanMontoTotal(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ej: 74000 o -74000 para descuentos"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cantidad de cuotas *</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={planCuotas}
                      onChange={(e) => setPlanCuotas(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Periodo de inicio</label>
                    <div className="grid grid-cols-2 gap-4">
                      <select
                        value={planInicioMes}
                        onChange={(e) => setPlanInicioMes(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {meses.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={planInicioAnio}
                        onChange={(e) => setPlanInicioAnio(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {anios.map((a) => (
                          <option key={a} value={a}>
                            {a}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500">
                    Ejemplo: {planDescripcionBase?.trim() || 'Cargo'} {formatearNumeroCuota(1)}/{formatearNumeroCuota(Number(planCuotas) || 1)}
                  </p>

                  <button
                    onClick={handleCrearPlan}
                    disabled={loadingPlan}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {loadingPlan ? 'Creando plan...' : 'Crear plan de cargos'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modal.isOpen}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        confirmText={modal.confirmText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
      />
    </div>
  );
};

export default CargosManualesClienteAdmin;
